import { createRouter, createWebHashHistory } from 'vue-router';
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw,
} from 'vue-router';
import { isUrl } from '@/utils/is';
import { omit, cloneDeep } from 'lodash-es';

const modules = import.meta.glob('../views/**/*.{vue,tsx}');

/* Layout */
export const Layout = () => import('@/layout/Layout.vue');

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout',
      });
    });
};

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) {
    return route;
  }
  const { matched, ...opt } = route;
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  };
};

// 后台返回菜单结构
interface Menus {
  url: string;
  childMenus?: Menus[];
  [key: string]: any;
}
/**
 * 根据后台返回的菜单数据匹配并排序路由
 *
 * 功能说明：
 * 1. 根据 menus 数组生成最终可访问的路由树（asyncRouterMap 是完整路由列表）。
 * 2. 子页面（meta.subPageKey）仅在 buttonList 中有权限时才加入。
 * 3. 如果父级菜单下所有子页面都被剔除，则父级菜单也会被剔除。
 * 4. 固定首页（meta.fixed = true）始终放在最前面。
 *
 * @param menus - 后台返回的菜单数组，每个菜单可能有子菜单
 * @param asyncRouterMap - 项目中所有的异步路由映射数组
 * @param buttonList - 用户按钮权限列表，结构为 [{ url: '/xxx' }]
 * @returns 返回匹配好的异步路由数组，按菜单顺序排序
 */
export function matchAndSortRoutes(
  menus: Menus[],
  asyncRouterMap: AppRouteRecordRaw[],
  buttonList: any[] = [],
): AppRouteRecordRaw[] {
  // 将按钮权限列表转成 Set，便于快速判断子页面是否有权限
  const buttonSet = new Set(buttonList.map((b) => (b.url || '').trim()));

  /**
   * 递归函数：根据 menus 构建路由树
   * @param menusList - 当前层级的菜单数组
   * @param routesList - 当前层级的可匹配路由数组
   */
  function buildRoutes(menusList: Menus[], routesList: AppRouteRecordRaw[]): AppRouteRecordRaw[] {
    const res: AppRouteRecordRaw[] = [];

    for (const menu of menusList) {
      const menuUrl = (menu.url || '').trim();

      // 在当前层级路由中找到对应菜单的路由
      const matched = routesList.find((r) => (r.path || '').trim() === menuUrl);
      if (!matched) {
        continue; // 没找到就跳过
      }

      // 浅拷贝一份路由，避免修改原始路由
      const clone: AppRouteRecordRaw = { ...matched, children: undefined };

      // 构建子路由
      if (matched.children && matched.children.length) {
        // 1. 按 menus 中的子菜单递归构建子路由
        const childrenFromMenu = menu.childMenus || [];
        const builtChildren = buildRoutes(childrenFromMenu, matched.children);

        // 2. 按按钮权限补充子页面（meta.subPageKey）
        const authorizedSubPages = (matched.children || []).filter(
          (r) => r.meta?.subPageKey && buttonSet.has(r.meta.subPageKey),
        );

        // 合并：先按菜单顺序，再加上有权限的子页面
        const allChildren = [...builtChildren, ...authorizedSubPages];

        if (allChildren.length) {
          clone.children = allChildren; // 有子路由才保留 children
        }
      }

      // 将当前菜单对应的路由加入结果
      res.push(clone);
    }

    return res;
  }

  // 根据 menus 构建完整路由树
  const matchedTree = buildRoutes(menus, asyncRouterMap);

  // 处理固定首页（fixed: true）放在前面
  const fixedRoutes = asyncRouterMap.filter((r) => r.meta?.fixed);
  const uniqueFixed = fixedRoutes.filter((f) => !matchedTree.some((m) => m.path === f.path));

  // 最终路由：固定首页 + 匹配菜单的路由树
  return [...uniqueFixed, ...matchedTree];
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/',
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = [];

  for (const route of routes) {
    const meta = route.meta ?? {};
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue;
    }

    let data: Nullable<AppRouteRecordRaw> = null;

    let onlyOneChild: Nullable<string> = null;
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string;
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route);
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim();
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route);
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path),
      );
    }
    if (data) {
      res.push(data as AppRouteRecordRaw);
    }
  }
  return res;
};

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = [];

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta,
    };
    if (route.component) {
      const comModule =
        modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`];
      const component = route.component as string;
      if (!comModule && !component.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`);
      } else {
        // 动态加载路由文件，可根据实际情况进行自定义逻辑
        let componentModule;
        if (component === '#') {
          componentModule = Layout;
        } else if (component.includes('##')) {
          componentModule = getParentLayout();
        } else {
          componentModule = comModule;
        }
        data.component = componentModule;
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children);
    }
    res.push(data as AppRouteRecordRaw);
  }
  return res;
};

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) {
    return path;
  }
  const childPath = path.startsWith('/') || !path ? path : `/${path}`;
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim();
};

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const clonedRoutes: AppRouteRecordRaw[] = cloneDeep(routes);
  for (let index = 0; index < clonedRoutes.length; index++) {
    const route = clonedRoutes[index];
    if (!isMultipleRoute(route)) {
      continue;
    }
    promoteRouteLevel(route);
  }
  return clonedRoutes;
};

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false;
  }

  const children = route.children;

  let flag = false;
  for (let index = 0; index < children.length; index++) {
    const child = children[index];
    if (child.children?.length) {
      flag = true;
      break;
    }
  }
  return flag;
};

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory(),
  });

  const routes = router.getRoutes();
  addToChildren(routes, route.children || [], route);
  router = null;

  route.children = route.children?.map((item) => omit(item, 'children'));
};

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw,
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index];
    const route = routes.find((item) => item.name === child.name);
    if (!route) {
      continue;
    }
    routeModule.children = routeModule.children || [];
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw);
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule);
    }
  }
};
